/**
 * FiscalBasisToggle — pick WHICH fiscal year a view's FY axis means.
 *
 * Lives in the header of every fiscal view, not in settings: the whole value
 * of the control is flipping the chart you are looking at and watching the
 * bars move. One shared context backs all of them, so a flip here changes
 * every other fiscal view and survives a reload.
 *
 * IT MAPS OVER THE REGISTRY. Two options today (State FY, PANW FY); when a
 * `fiscal_calendars` table lands with a row per tech vendor, this renders N
 * options with no redesign — above ~4 it becomes a <select> on its own so the
 * strip never overflows a panel header.
 */

import React, { useCallback } from 'react';
import { fiscalSpanLabel } from '@techstack/core';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';
import logger from '../../lib/logger';
import './FiscalBasisToggle.css';

/** Past this many calendars a segmented strip stops fitting a panel header. */
const MAX_SEGMENTS = 4;

/**
 * Says out loud what the toggle does NOT reach. The competitor and
 * displacement rollups read a materialized view whose fiscal year is baked to
 * the July boundary, and re-bucketing them would mean the all-vendor per-row
 * scan that blew the production query timeout — so they stay on the state
 * window whatever is selected here. Better to admit that than to let their
 * totals quietly disagree with the chart.
 */
const SCOPE_HINT =
  'Sets the fiscal year for spend charts, the KPI strip and renewal horizons. '
  + 'Competitor and displacement rollups stay on the California state window.';

/**
 * @param {object} props
 * @param {string} [props.surface] - view name, for the interaction log only.
 * @param {string} [props.className]
 */
function FiscalBasisToggle({ surface = 'unknown', className = '' }) {
  const { basis, setBasis, calendars } = useFiscalBasisContext();

  const change = useCallback((next) => {
    if (next === basis) return;
    logger.vendorAnalysis.info('FiscalBasisToggle:change', {
      surface, from: basis, to: next,
    });
    setBasis(next);
  }, [basis, setBasis, surface]);

  // A one-calendar registry has nothing to choose between — render nothing
  // rather than a dead single-button strip.
  if (!calendars || calendars.length < 2) return null;

  const describe = (c) => `${c.label} — ${fiscalSpanLabel(c)}`;

  if (calendars.length > MAX_SEGMENTS) {
    return (
      <label className={`fbt fbt--select ${className}`}>
        <span className="fbt__legend" title={SCOPE_HINT}>Fiscal year</span>
        <select
          className="fbt__select"
          value={basis}
          onChange={(e) => change(e.target.value)}
        >
          {calendars.map(c => (
            <option key={c.key} value={c.key}>{describe(c)}</option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <div
      className={`fbt ${className}`}
      role="radiogroup"
      aria-label="Fiscal year basis"
    >
      <span className="fbt__legend" title={SCOPE_HINT}>Fiscal year</span>
      <div className="fbt__group">
        {calendars.map(c => (
          <button
            key={c.key}
            type="button"
            role="radio"
            aria-checked={c.key === basis}
            className={`fbt__option ${c.key === basis ? 'is-selected' : ''}`}
            onClick={() => change(c.key)}
            title={describe(c)}
          >
            <span className="fbt__option-name">{c.shortLabel}</span>
            <span className="fbt__option-span">{fiscalSpanLabel(c)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default FiscalBasisToggle;
